import { useCallback, useEffect, useRef, useState } from 'preact/hooks'
import type { ChatMode, Message } from './types'
import * as api from './api'

const PRIMARY = '#0b487b'
const SECONDARY = '#F97316'
const STORAGE_KEY = 'owner-chat-state'

function loadState() {
	try {
		const saved = localStorage.getItem(STORAGE_KEY)
		if (!saved) return null
		const parsed = JSON.parse(saved)
		// Expire after 24 hours
		if (Date.now() - parsed.savedAt > 24 * 60 * 60 * 1000) {
			localStorage.removeItem(STORAGE_KEY)
			return null
		}
		return parsed
	} catch {
		return null
	}
}

function saveState(messages: Message[], mode: ChatMode, threadTs: string | null) {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify({ messages, mode, threadTs, savedAt: Date.now() }))
	} catch {}
}

export function App() {
	const saved = loadState()
	const [isOpen, setIsOpen] = useState(false)
	const [mode, setMode] = useState<ChatMode>(saved?.mode || 'ai')
	const [teamOnline, setTeamOnline] = useState(false)
	const [messages, setMessages] = useState<Message[]>(saved?.messages || [])
	const [input, setInput] = useState('')
	const [loading, setLoading] = useState(false)
	const [threadTs, setThreadTs] = useState<string | null>(saved?.threadTs || null)
	const messagesEndRef = useRef<HTMLDivElement>(null)
	const pollRef = useRef<number | null>(null)

	// Persist state to localStorage
	useEffect(() => {
		saveState(messages, mode, threadTs)
	}, [messages, mode, threadTs])

	// Check team status on mount
	useEffect(() => {
		api.getStatus().then((s) => setTeamOnline(s.online))
		const interval = setInterval(() => {
			api.getStatus().then((s) => setTeamOnline(s.online))
		}, 60000)
		return () => clearInterval(interval)
	}, [])

	// Auto-scroll to bottom
	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
	}, [messages])

	// Poll Slack for human replies
	useEffect(() => {
		if (mode !== 'human' || !threadTs) return
		const poll = () => {
			api.getSlackMessages(threadTs).then((slackMsgs) => {
				const teamReplies = slackMsgs.filter((m) => m.user && !m.text.startsWith('[Bot]'))
				if (teamReplies.length === 0) return
				setMessages((prev) => {
					const existingIds = new Set(prev.map((m) => m.id))
					const newMsgs = teamReplies
						.filter((msg) => !existingIds.has(`slack-${msg.ts}`))
						.map((msg) => ({
							id: `slack-${msg.ts}`,
							role: 'assistant' as const,
							content: msg.text,
							timestamp: Date.now(),
						}))
					return newMsgs.length > 0 ? [...prev, ...newMsgs] : prev
				})
			})
		}
		pollRef.current = window.setInterval(poll, 4000)
		return () => {
			if (pollRef.current) clearInterval(pollRef.current)
		}
	}, [mode, threadTs])

	const addMessage = useCallback((role: Message['role'], content: string) => {
		setMessages((prev) => [
			...prev,
			{ id: `msg-${Date.now()}`, role, content, timestamp: Date.now() },
		])
	}, [])

	const handleSend = useCallback(async () => {
		const text = input.trim()
		if (!text || loading) return

		setInput('')
		addMessage('user', text)

		if (mode === 'human' && threadTs) {
			// In human mode, send to Slack thread
			setLoading(true)
			try {
				await api.sendToSlack(
					[{ id: 'msg', role: 'user', content: text, timestamp: Date.now() }],
					{}
				)
			} catch {
				// Message still shows locally
			}
			setLoading(false)
			return
		}

		// AI mode
		setLoading(true)
		try {
			const allMessages = [
				...messages.map((m) => ({ role: m.role, content: m.content })),
				{ role: 'user', content: text },
			]
			const response = await api.sendAiMessage(allMessages)

			if (response.tool_call?.name === 'handoff_to_human') {
				addMessage('assistant', response.reply)
				// Handoff to Slack
				const updatedMessages = [
					...messages,
					{ id: 'msg', role: 'user' as const, content: text, timestamp: Date.now() },
					{ id: 'msg2', role: 'assistant' as const, content: response.reply, timestamp: Date.now() },
				]
				const slackResult = await api.sendToSlack(updatedMessages, {})
				setThreadTs(slackResult.thread_ts)
				setMode('human')
				addMessage('system', 'You are now connected with our team. They will reply shortly.')
			} else if (response.tool_call?.name === 'create_lead') {
				addMessage('assistant', response.reply)
			} else {
				addMessage('assistant', response.reply)
			}
		} catch {
			addMessage('assistant', 'Sorry, something went wrong. Please call us on 1300 471 805.')
		}
		setLoading(false)
	}, [input, loading, mode, messages, threadTs, addMessage])

	const handleHandoff = useCallback(async () => {
		if (!teamOnline) {
			setMode('offline')
			addMessage('system', 'Our team is currently offline. Please leave your details and we will get back to you.')
			return
		}
		setLoading(true)
		try {
			addMessage('system', 'Connecting you with our team...')
			const result = await api.sendToSlack(messages, {})
			setThreadTs(result.thread_ts)
			setMode('human')
			addMessage('system', 'You are now connected. A team member will reply shortly.')
		} catch {
			addMessage('system', 'Could not connect. Please call us on 1300 471 805.')
		}
		setLoading(false)
	}, [messages, teamOnline, addMessage])

	const handleOpen = useCallback(() => {
		setIsOpen(true)
		if (messages.length === 0) {
			addMessage('assistant', 'Hi! I\'m the Owner Inspections assistant. Ask me anything about our building inspection services, pricing, or coverage areas.')
		}
	}, [messages.length, addMessage])

	return (
		<>
			{/* Chat Bubble */}
			{!isOpen && (
				<button
					onClick={handleOpen}
					style={{
						position: 'fixed',
						bottom: '20px',
						right: '20px',
						width: '60px',
						height: '60px',
						borderRadius: '50%',
						backgroundColor: PRIMARY,
						border: 'none',
						cursor: 'pointer',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
						transition: 'transform 0.2s',
					}}
					onMouseEnter={(e) => {
						;(e.currentTarget as HTMLElement).style.transform = 'scale(1.1)'
					}}
					onMouseLeave={(e) => {
						;(e.currentTarget as HTMLElement).style.transform = 'scale(1)'
					}}
				>
					<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
						<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
					</svg>
					{/* Online dot */}
					{teamOnline && (
						<span
							style={{
								position: 'absolute',
								top: '2px',
								right: '2px',
								width: '14px',
								height: '14px',
								borderRadius: '50%',
								backgroundColor: '#22c55e',
								border: '2px solid white',
							}}
						/>
					)}
				</button>
			)}

			{/* Chat Window */}
			{isOpen && (
				<div
					style={{
						position: 'fixed',
						bottom: '20px',
						right: '20px',
						width: '380px',
						maxWidth: 'calc(100vw - 32px)',
						height: '520px',
						maxHeight: 'calc(100vh - 40px)',
						borderRadius: '16px',
						overflow: 'hidden',
						boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
						display: 'flex',
						flexDirection: 'column',
						backgroundColor: '#f9fafb',
					}}
				>
					{/* Header */}
					<div
						style={{
							backgroundColor: PRIMARY,
							color: 'white',
							padding: '14px 16px',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							flexShrink: 0,
						}}
					>
						<div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
							<div
								style={{
									width: '36px',
									height: '36px',
									borderRadius: '50%',
									backgroundColor: 'rgba(255,255,255,0.2)',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									fontSize: '16px',
									fontWeight: 'bold',
								}}
							>
								OI
							</div>
							<div>
								<div style={{ fontWeight: '600', fontSize: '15px' }}>Owner Inspections</div>
								<div style={{ fontSize: '12px', opacity: 0.8, display: 'flex', alignItems: 'center', gap: '4px' }}>
									<span
										style={{
											width: '7px',
											height: '7px',
											borderRadius: '50%',
											backgroundColor: teamOnline ? '#22c55e' : '#9ca3af',
											display: 'inline-block',
										}}
									/>
									{mode === 'human' ? 'Talking to team' : teamOnline ? 'Online' : 'Offline'}
								</div>
							</div>
						</div>
						<button
							onClick={() => setIsOpen(false)}
							style={{
								background: 'none',
								border: 'none',
								color: 'white',
								cursor: 'pointer',
								fontSize: '20px',
								padding: '4px',
								lineHeight: 1,
							}}
						>
							&#x2715;
						</button>
					</div>

					{/* Messages */}
					<div
						style={{
							flex: 1,
							overflowY: 'auto',
							padding: '12px',
							display: 'flex',
							flexDirection: 'column',
							gap: '8px',
						}}
					>
						{messages.map((msg) => (
							<div
								key={msg.id}
								style={{
									display: 'flex',
									justifyContent:
										msg.role === 'user' ? 'flex-end' : msg.role === 'system' ? 'center' : 'flex-start',
								}}
							>
								<div
									style={{
										maxWidth: msg.role === 'system' ? '90%' : '80%',
										padding: msg.role === 'system' ? '6px 12px' : '10px 14px',
										borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : msg.role === 'system' ? '8px' : '16px 16px 16px 4px',
										backgroundColor:
											msg.role === 'user' ? PRIMARY : msg.role === 'system' ? '#e5e7eb' : 'white',
										color: msg.role === 'user' ? 'white' : msg.role === 'system' ? '#6b7280' : '#1f2937',
										fontSize: msg.role === 'system' ? '12px' : '14px',
										lineHeight: '1.5',
										boxShadow: msg.role !== 'system' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
										fontStyle: msg.role === 'system' ? 'italic' : 'normal',
										whiteSpace: 'pre-wrap',
									}}
								>
									{msg.content}
								</div>
							</div>
						))}
						{loading && (
							<div style={{ display: 'flex', justifyContent: 'flex-start' }}>
								<div
									style={{
										padding: '10px 14px',
										borderRadius: '16px 16px 16px 4px',
										backgroundColor: 'white',
										color: '#9ca3af',
										fontSize: '14px',
										boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
									}}
								>
									Typing...
								</div>
							</div>
						)}
						<div ref={messagesEndRef} />
					</div>

					{/* Talk to person button */}
					{mode === 'ai' && messages.length > 1 && (
						<div style={{ padding: '0 12px 8px', flexShrink: 0 }}>
							<button
								onClick={handleHandoff}
								style={{
									width: '100%',
									padding: '8px',
									border: `1px solid ${PRIMARY}`,
									borderRadius: '8px',
									backgroundColor: 'white',
									color: PRIMARY,
									fontSize: '13px',
									cursor: 'pointer',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									gap: '6px',
								}}
							>
								<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
									<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
									<circle cx="12" cy="7" r="4" />
								</svg>
								Talk to a person {teamOnline && <span style={{ color: '#22c55e', fontSize: '11px' }}>(online)</span>}
							</button>
						</div>
					)}

					{/* Input */}
					<div
						style={{
							padding: '12px',
							borderTop: '1px solid #e5e7eb',
							backgroundColor: 'white',
							display: 'flex',
							gap: '8px',
							flexShrink: 0,
						}}
					>
						<input
							type="text"
							value={input}
							onInput={(e) => setInput((e.target as HTMLInputElement).value)}
							onKeyDown={(e) => e.key === 'Enter' && handleSend()}
							placeholder={mode === 'human' ? 'Message the team...' : 'Ask a question...'}
							style={{
								flex: 1,
								padding: '10px 14px',
								border: '1px solid #d1d5db',
								borderRadius: '24px',
								fontSize: '14px',
								outline: 'none',
								backgroundColor: '#f9fafb',
							}}
						/>
						<button
							onClick={handleSend}
							disabled={loading || !input.trim()}
							style={{
								width: '40px',
								height: '40px',
								borderRadius: '50%',
								backgroundColor: SECONDARY,
								border: 'none',
								cursor: loading || !input.trim() ? 'default' : 'pointer',
								opacity: loading || !input.trim() ? 0.5 : 1,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								flexShrink: 0,
							}}
						>
							<svg width="18" height="18" viewBox="0 0 24 24" fill="white">
								<path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
							</svg>
						</button>
					</div>
				</div>
			)}
		</>
	)
}
